/**
 * ELF-CIRCLE palette + light/dark controller.
 *
 * The design ships nine colour families, each with a dark and a light palette.
 * Picking a family and a mode resolves to exactly one `data-palette` value,
 * which the CSS in `elf-landing.css` keys off.
 *
 * The mode is kept in sync with UIKit's `useTheme()` so the rest of the app
 * (header, dialogs, toasts) flips together with the landing page. UIKit's
 * ThemeProvider is mounted with `forceConfig`, which disables its own
 * persistence — so the choice is persisted here instead.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@bloomneo/uikit';

export type ElfMode = 'dark' | 'light';

export interface ElfFamily {
  /** Label shown in the palette bar. */
  label: string;
  /** Swatch colour for the bar's dot. */
  swatch: string;
  /** `data-palette` value used in dark mode. */
  dark: string;
  /** `data-palette` value used in light mode. */
  light: string;
}

/** Order matches the design's palette bar, left to right. */
export const ELF_FAMILIES: ElfFamily[] = [
  { label: 'Ember', swatch: '#EB9027', dark: 'ember', light: 'ivory' },
  { label: 'Sungold', swatch: '#F3B806', dark: 'sungold', light: 'sungold-light' },
  { label: 'Ocean', swatch: '#0296C5', dark: 'ocean', light: 'ocean-light' },
  { label: 'Midnight', swatch: '#38BDF8', dark: 'midnight', light: 'cloud' },
  { label: 'Forest', swatch: '#34D399', dark: 'forest', light: 'mint' },
  { label: 'Blossom', swatch: '#EE70AE', dark: 'blossom', light: 'blush' },
  { label: 'Signal', swatch: '#E22643', dark: 'signal', light: 'sand' },
  { label: 'Lavender', swatch: '#A78BFA', dark: 'lavender-dark', light: 'lavender' },
  { label: 'Mono', swatch: '#C9C4B8', dark: 'mono', light: 'paper' },
];

const STORAGE_KEY = 'elf-landing-theme';

interface StoredPreference {
  family: number;
  mode: ElfMode;
}

function readStored(): StoredPreference | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPreference>;
    const family =
      typeof parsed.family === 'number' && parsed.family >= 0 && parsed.family < ELF_FAMILIES.length
        ? parsed.family
        : 0;
    const mode: ElfMode = parsed.mode === 'light' ? 'light' : 'dark';
    return { family, mode };
  } catch {
    return null;
  }
}

export interface ElfPalette {
  family: number;
  mode: ElfMode;
  /** Resolved `data-palette` value for the current family + mode. */
  palette: string;
  setFamily: (index: number) => void;
  setMode: (mode: ElfMode) => void;
  /**
   * Ref callback for the landing root. Applies `data-palette` imperatively and
   * freezes transitions for one frame so colours snap to the new palette
   * instead of easing from the old one (custom-property changes don't restart
   * CSS transitions on their own).
   */
  rootRef: (node: HTMLDivElement | null) => void;
}

export function useElfPalette(): ElfPalette {
  const stored = readStored();
  const [family, setFamilyState] = useState<number>(stored?.family ?? 0);
  const [mode, setModeState] = useState<ElfMode>(stored?.mode ?? 'dark');
  const [root, setRoot] = useState<HTMLDivElement | null>(null);

  const { setMode: setUikitMode } = useTheme();

  const palette = mode === 'dark' ? ELF_FAMILIES[family].dark : ELF_FAMILIES[family].light;

  // Apply the palette, with a one-frame transition freeze around the swap.
  useEffect(() => {
    if (!root) return;
    root.classList.add('nopaint');
    root.setAttribute('data-palette', palette);
    // Force a reflow so the frozen state is committed before we unfreeze.
    void root.offsetWidth;
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove('nopaint'))
    );
    return () => cancelAnimationFrame(raf);
  }, [root, palette]);

  // Keep UIKit's theme mode and the document background in step with the hero.
  //
  // UIKit rebuilds `setMode` on every ThemeProvider render and its setter always
  // stores a fresh state object, so depending on the function identity here
  // would loop: effect -> setState -> provider render -> new identity -> effect.
  // Hold the latest setter in a ref and sync only when `mode` actually changes.
  const setUikitModeRef = useRef(setUikitMode);
  setUikitModeRef.current = setUikitMode;

  useEffect(() => {
    setUikitModeRef.current(mode);
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ family, mode }));
    } catch {
      /* storage unavailable (private mode / quota) — the choice just won't persist */
    }
  }, [family, mode]);

  const setFamily = useCallback((index: number) => {
    if (index >= 0 && index < ELF_FAMILIES.length) setFamilyState(index);
  }, []);

  const setMode = useCallback((next: ElfMode) => setModeState(next), []);

  return { family, mode, palette, setFamily, setMode, rootRef: setRoot };
}
